import React, { FC } from 'react';
import filter from 'lodash/filter';
import get from 'lodash/get';
import map from 'lodash/map';
import omit from 'lodash/omit';
import orderBy from 'lodash/orderBy';
import set from 'lodash/set';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import Dropdown from 'src/presentation/components/forms/Dropdown';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { useForm, FormEditorProps } from 'src/presentation/components/forms/Form';
import InputField from 'src/presentation/components/forms/InputField';
import MultiselectChips from 'src/presentation/components/forms/MultiselectChips';
import RadioGroup from 'src/presentation/components/forms/RadioGroup';
import SlaMinutesInputField from './components/SlaMinutesInputField';
import agentsResource from 'src/actions/agent/agentsResource';
import endpointsResource from 'src/actions/endpoints/endpointsResource';
import generateId from 'src/lib/generateId';
import routingGroupsResource from 'src/actions/routingGroups/routingGroupsResource';
import { useResourceValues } from 'src/presentation/hooks/useResource';
import { AlertInfo } from '@ad-scripts/presentation/components/common/alert';
import { Body, Row } from 'src/presentation/components/forms/Layout';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { ColumnRow } from '../chat/ChatLayout';
import { EndpointTypes } from 'src/models/Channels/endpointType';
import { H1, H2 } from '@ad-scripts/presentation/components/common/headers';
import { StyledExternalLink } from 'src/presentation/components/pages/shared/ExternalLink';
import { getAdminURL } from 'src/presentation/components/pages/shared/utils/Utils';
import { routingGroupDefault } from 'src/models/RoutingGroup/RoutingGroup';

enum InboxState {
  ACTIVE = 'active',
  DEACTIVATED = 'deactivated',
}

const useOnSave = () => {
  const dispatch = useDispatch();
  return ({ add, isNew, item, resetErrors, update, validate, workingStateRef }) => {
    dispatch(resetErrors());

    const currentWorkingState = workingStateRef.current;

    const workingStateToUpdate = {
      ...omit(currentWorkingState, ['agentNames', 'slaString']),
      id: isNew ? generateId() : currentWorkingState.id,
    };

    if (!dispatch(validate(workingStateToUpdate))) {
      return;
    }

    isNew ? dispatch(add({}, workingStateToUpdate)) : dispatch(update({ id: item.id }, workingStateToUpdate));
  };
};

const renderEndpointsInfo = entryPointsInfo => {
  if (!entryPointsInfo) {
    return '';
  }
  const config = [
    { count: entryPointsInfo[EndpointTypes.CHAT], title: 'chat' },
    { count: entryPointsInfo[EndpointTypes.EMAIL], title: 'email' },
    { count: entryPointsInfo[EndpointTypes.SMS], title: 'SMS' },
    { count: entryPointsInfo[EndpointTypes.VOICE], title: 'voice' },
    { count: entryPointsInfo[EndpointTypes.FB_MESSENGER], title: 'facebook messenger' },
    { count: entryPointsInfo[EndpointTypes.INSTAGRAM_DIRECT], title: 'instagram messaging' },
    { count: entryPointsInfo[EndpointTypes.TWITTER], title: 'twitter direct messages' },
    { count: entryPointsInfo[EndpointTypes.WHATSAPP], title: 'WhatsApp' },
  ];
  return config
    .filter(({ count }) => count)
    .map(({ count, title }, i, list) => {
      const andWord = list.length > 1 && i + 1 === list.length ? 'and ' : '';
      const commaSymbol = list.length >= 3 && i > 0 ? ',' : '';
      return `${commaSymbol} ${andWord}${count} ${title}`;
    })
    .join('');
};

const InboxEditor: FC<FormEditorProps> = ({ errors, item, root }) => {
  const { values: agents } = useResourceValues(agentsResource);
  const { add, update, validate } = routingGroupsResource.effects;
  const { resetErrors } = routingGroupsResource.actions;
  const { values: endpoints } = useResourceValues(endpointsResource);
  const SUPPORTED_ENDPOINT_TYPES = ['EMAIL', 'VOICE', 'SMS', 'WHATSAPP', 'TWITTER'];

  const dropdownOptionsForSupportedTypes: any = SUPPORTED_ENDPOINT_TYPES.reduce(
    (memo, type) => ({
      ...memo,
      [type]: [
        { label: 'None', value: '' },
        ...map(filter(endpoints, { type }), ({ id, address }) => ({ value: id, label: address })),
      ],
    }),
    {}
  );

  const agentOptions = orderBy(
    map(agents, ({ id, name, email = '' }) => ({
      value: id,
      label: name || email,
      description: name ? email : '',
    })),
    ['label']
  );

  const formContext = useForm({
    add,
    defaultItem: routingGroupDefault,
    errors,
    item,
    onSave: useOnSave(),
    resetErrors,
    root,
    update,
    validate,
  });

  const { isNew, onClose, onSave, workingState, updateState } = formContext;
  const header = isNew ? 'Create Inbox' : `Edit ${item.name}`;
  const endpointsInfoString = renderEndpointsInfo(item?.entryPointsInfo);

  const stateValue = get(workingState, 'disabled') ? InboxState.DEACTIVATED : InboxState.ACTIVE;
  const onStateChangeHandle = event => {
    const value = event.target.value === InboxState.DEACTIVATED;
    updateState(set({}, 'disabled', value));
  };

  const channelsEntryPointsUrl = getAdminURL('entry-points');

  return (
    <Form context={formContext}>
      <H1>{header}</H1>

      <Body data-aid="inbox-editor">
        <Row>
          <InputField attr="name" data-aid="name" label="Name" placeholder="i.e. Escalations Team" />
        </Row>
        <StyledRow>
          <StyledLabel>Status</StyledLabel>
          {endpointsInfoString && (
            <StyledAlertInfo>
              <b>Note:&nbsp;</b>
              This Inbox is linked to at least one Entry Point. Disconnect this Inbox from {endpointsInfoString} Entry
              Points and try again. {` `}
              <StyledExternalLink
                data-aid="entry-points-link"
                to={channelsEntryPointsUrl}
                showIcon={true}
                label="Go to Entry Points"
              />
            </StyledAlertInfo>
          )}

          <RadioGroup
            attr=""
            disabled={Boolean(endpointsInfoString)}
            className="radioGroup"
            data-aid="enabled-radio-group"
            onChange={onStateChangeHandle}
            options={[
              {
                label: 'Active',
                value: InboxState.ACTIVE,
              },
              {
                label: 'Deactivated',
                value: InboxState.DEACTIVATED,
              },
            ]}
            value={stateValue}
          />
        </StyledRow>
        <Row>
          <SlaMinutesInputField
            attr="slaSeconds"
            data-aid="slaSeconds"
            description="Enter your company's service level agreement (SLA) in minutes. Partial minute decimal values are allowed (e.g. 0.66 is equal to 40 sec)."
            label="SLA"
            placeholder="Enter a number"
          />
        </Row>
        <Row>
          <MultiselectChips
            attr="agentIds"
            data-aid="agent-selector"
            description="Enter the name or email address for the agents you’d like to assign to this Inbox."
            label="Agents"
            options={agentOptions}
          />
        </Row>
        <ColumnRow>
          <H2>Default ‘from’ contact addresses</H2>
          <GroupDescription>
            {`Set default 'from' contact addresses for all Channels supported by your Inbox. This ensures that customer
            replies are directed to the correct Inbox.`}
          </GroupDescription>
        </ColumnRow>
        <Row>
          <Dropdown
            allowEmptyValues
            attr="defaultEndpoints.EMAIL"
            data-aid="default-email-address"
            description="The primary email address new outbound messages will be sent from. "
            fitOpenerWidth={false}
            label="Default Email Address"
            options={dropdownOptionsForSupportedTypes.EMAIL}
            placeholder="None"
            searchable
          />
        </Row>
        <Row>
          <Dropdown
            attr="defaultEndpoints.SMS"
            data-aid="default-sms-number"
            description="The primary SMS phone number messages will be sent from."
            allowEmptyValues
            fitOpenerWidth={false}
            label="Default SMS Number"
            options={dropdownOptionsForSupportedTypes.SMS}
            placeholder="None"
            searchable
          />
        </Row>
        <Row>
          <Dropdown
            attr="defaultEndpoints.VOICE"
            data-aid="default-phone-number"
            description="The primary phone number calls will be initiated from. "
            allowEmptyValues
            fitOpenerWidth={false}
            label="Default Voice Number"
            options={dropdownOptionsForSupportedTypes.VOICE}
            placeholder="None"
            searchable
          />
        </Row>
        <Row>
          <Dropdown
            attr="defaultEndpoints.WHATSAPP"
            data-aid="default-whatsapp-number"
            description="The primary phone number WhatsApp messages will be sent from."
            allowEmptyValues
            fitOpenerWidth={false}
            label="Default WhatsApp Number"
            options={dropdownOptionsForSupportedTypes.WHATSAPP}
            placeholder="None"
            searchable
          />
        </Row>
      </Body>

      <Footer>
        <Button data-aid="editor-cancel" type={Button.Types.TEXT} onClick={onClose}>
          Cancel
        </Button>
        <Button data-aid="editor-save" onClick={onSave}>
          Save
        </Button>
      </Footer>
    </Form>
  );
};

const GroupDescription = styled.span`
  color: ${p => p.theme.colors.gray600};
`;

const StyledRow = styled.div`
  margin-bottom: 12px;
`;

const StyledLabel = styled.b`
  display: block;
  margin-bottom: 8px;
`;

const StyledAlertInfo = styled(AlertInfo)`
  margin-bottom: 12px;
  line-height: 20px;
`;

export default InboxEditor;
